// src/components/classroom/components/backup/instructorModeScreens/Trainer.tsx

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
// import p1 from "../../../../assets/images/p1.png";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { IoMicOutline, IoMicOffOutline } from "react-icons/io5";
import {
  HiOutlineVideoCamera,
  HiOutlineVideoCameraSlash,
} from "react-icons/hi2";
import { Text } from "@radix-ui/themes";

interface ParticipantProps {
  name: string;
  trainerTableName: string | null;
  videoWidth?: number | string;
  videoHeight?: number | string;
  instructorMode?: boolean;
  peerMode?: boolean;
  isAudio?: boolean;
}

const Trainer: React.FC<ParticipantProps> = ({
  name = "Trainer Name",
  trainerTableName,
  videoWidth = 192, // Default width in pixels
  videoHeight = 144,
  instructorMode = false,
  peerMode = false,
  isAudio = true,
}) => {
  const [micOn, setMicOn] = useState(false);
  const [videoOn, setVideoOn] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const currentVideoTrackRef = useRef<MediaStreamTrack | null>(null);
  const currentAudioTrackRef = useRef<MediaStreamTrack | null>(null);
  const trainerTable = useSelector((state: RootState) => state.live.settings.trainerJoinedtable);
  const trainerJoinedtable = useSelector((state: RootState) => state.live.settings.trainerJoinedtable);
  const consumers = useSelector(
    (state: RootState) => state.live.settings.consumers
  );

  const consumersArray: any[] = useMemo(() => {
    return Object.values(consumers || {});
  }, [consumers]);

  // Memoize the consumer finders to avoid recomputing on every render
  const getVideoConsumer = useCallback(() => {
    return consumersArray.find(
      (consumer: any) =>
        consumer.peerId === "trainer@vtt" && consumer.kind === "video"
    );
  }, [consumersArray]);

  const getAudioConsumer = useCallback(() => {
    return consumersArray.find(
      (consumer: any) =>
        consumer.peerId === "trainer@vtt" && consumer.kind === "audio"
    );
  }, [consumersArray]);

  // Separate effects for video and audio to isolate updates
  useEffect(() => {
    const videoConsumer = getVideoConsumer();

    const shouldShowVideo =
      videoConsumer &&
      videoConsumer.track &&
      videoConsumer.track.readyState === "live" &&
      !videoConsumer.locallyPaused &&
      !videoConsumer.remotelyPaused &&
      !(videoConsumer.appData?.share === true);

    if (shouldShowVideo) {
      const newTrack = videoConsumer.track;
      // Only update if the track has changed or is different
      if (currentVideoTrackRef.current?.id !== newTrack.id) {
        setTimeout(() => {
          if (videoRef.current && newTrack) {
            const stream = new MediaStream([newTrack]);
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch((error) => console.error('Trainer Video play error:', error));
            currentVideoTrackRef.current = newTrack;
          }
        }, 100);
      }
      setVideoOn(true);
    } else {
      // Only stop if it was previously on
      if (videoOn) {
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.srcObject = null;
        }
        currentVideoTrackRef.current = null;
        setVideoOn(false);
      }
    }
  }, [getVideoConsumer]); // Depend only on the memoized getter

  // useEffect(() => {
  //   const audioConsumer = getAudioConsumer();

  //   const shouldShowAudio =
  //     audioConsumer &&
  //     audioConsumer.track &&
  //     audioConsumer.track.readyState === "live" &&
  //     !audioConsumer.locallyPaused &&
  //     !audioConsumer.remotelyPaused;

  //   if (shouldShowAudio) {
  //     const newTrack = audioConsumer.track;
  //     // Only update if the track has changed or is different
  //     if (currentAudioTrackRef.current?.id !== newTrack.id) {
  //       setTimeout(() => {
  //         if (audioRef.current && newTrack) {
  //           const stream = new MediaStream([newTrack]);
  //           audioRef.current.srcObject = stream;
  //           audioRef.current
  //             .play().then(() => console.log('Video play success'))
  //             .catch(err => {
  //               console.error('Video play error:', err);
  //               if (err.name === 'AbortError') {
  //                 console.log('Ignoring AbortError on play');
  //               }
  //             });
  //           currentAudioTrackRef.current = newTrack;
  //         }
  //       }, 100);
  //     }
  //     setMicOn(true);
  //   } else {
  //     // Only stop if it was previously on
  //     if (micOn) {
  //       if (audioRef.current) {
  //         audioRef.current.pause();
  //         audioRef.current.srcObject = null;
  //       }
  //       currentAudioTrackRef.current = null;
  //       setMicOn(false);
  //     }
  //   }
  // }, [getAudioConsumer]); // Depend only on the memoized getter

  // Cleanup on unmount
  // useEffect(() => {
  //   return () => {
  //     if (videoRef.current) {
  //       videoRef.current.pause();
  //       videoRef.current.srcObject = null;
  //     }
  //     if (audioRef.current) {
  //       audioRef.current.pause();
  //       audioRef.current.srcObject = null;
  //     }
  //   };
  // }, []);

  const getTailwindSize = (size: number | string, isWidth: boolean): string => {
    if (typeof size === 'string') {
      // Validate that it's a Tailwind class or convert percentage to Tailwind
      if (size === '100%') return isWidth ? 'w-full' : 'h-full';
      if (size === '90%') return isWidth ? 'w-full' : 'h-[90%]';
      if (size === '75vh') return isWidth ? 'w-full' : 'h-[75vh]';
      return size;
    }
    const sizeMap: { [key: number]: string } = isWidth
      ? {
        192: 'w-48',
        128: 'w-32',
        256: 'w-64',
        320: 'w-80',
        384: 'w-96',
      }
      : {
        144: 'h-36',
        96: 'h-24',
        192: 'h-48',
        240: 'h-60',
        288: 'h-72',
        290: 'h-[55vh]',
        300: 'h-[75vh]',
      };
    return sizeMap[size] || (isWidth ? 'w-48' : 'h-36');
  };

  const containerClasses = [
    'relative flex justify-center items-center',
    instructorMode
      ? 'w-[99%] h-[70vh] mobile:w-full mobile:h-[45%]'
      : [
        getTailwindSize(videoWidth, true),
        getTailwindSize(videoHeight, false),
        'mobile:w-48 mobile:h-28 ',
        'tablet:w-56 tablet:h-32',
        'laptop:w-56 laptop:h-32',
        'desktop:w-64 desktop:h-32',
        'largescreen:w-64 largescreen:h-32',
      ].join(' '),
    'ring-2 ring-white shadow-inner rounded-lg',
  ].join(' ');

  const containerClassesNew = [
    'relative flex justify-center items-center',
    instructorMode
      ? 'w-full h-[70vh] mobile:w-full mobile:h-48'
      : [
        getTailwindSize(videoWidth, true),
        getTailwindSize(videoHeight, false),
        'mobile:h-72 tablet:h-[60vh] laptop:h-[65vh] desktop:h-[70vh]'
      ].join(' ')
  ].join(' ');

  // const getDimension = (dim: number | string | undefined, isWidth: boolean = false): string => {
  //   if (dim === undefined) return isWidth ? `${videoWidth}px` : `${videoHeight}px`;
  //   if (typeof dim === 'number') return `${dim}px`;
  //   return dim;
  // };

  // const containerWidth = getDimension(videoWidth, true);
  // const containerHeight = getDimension(videoHeight);

  return (
    <div className="flex flex-col items-center justify-center w-full h-full relative">
      {/* Video/Image container */}
      <div className={peerMode ? containerClassesNew : containerClasses}>
        {videoOn ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`${peerMode ? 'mobile:object-cover object-cover rounded w-full h-full' : 'mobile:object-contain object-cover rounded w-full h-full'}`}
          />
        ) : (
          <div className="w-full h-full rounded-lg" style={{ borderRadius: '8px', background: '#5B5D6B' }}>
            <Text className="w-full h-full flex items-center justify-center text-white font-bold mobile:text-sm tablet:text-lg laptop:text-xl bg-radial-gradient bg-blend-multiply rounded-lg" style={{ borderRadius: '8px', background: 'radial-gradient(50% 86.76% at 50% 50%, rgba(33, 32, 37, 0.00) 0%, rgba(33, 32, 37, 0.30) 50%, rgba(33, 32, 37, 0.60) 100%)', backgroundBlendMode: 'multiply', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFF', fontSize: '20px', fontWeight: '700', }}>
              {name
                ?.split(' ')
                ?.map((word: string) => word.charAt(0))
                ?.slice(0, 2)
                ?.join('') || 'TR'}
            </Text>
          </div>
        )}

        {trainerJoinedtable?.isTrainerPresent && !peerMode && (
          <div className="absolute inset-0 bg-black/60 flex flex-col justify-center items-center text-center rounded-lg">
            <p className="text-white font-semibold text-lg">
              Joined {trainerJoinedtable?.groupName}
            </p>
          </div>
        )}

        {/* Mic and camera indicators */}
        {/* <div className="absolute bottom-1 left-1 flex space-x-2 text-white text-lg">
          {videoOn ? <HiOutlineVideoCamera /> : <HiOutlineVideoCameraSlash />}
          {micOn ? <IoMicOutline /> : <IoMicOffOutline />}
        </div> */}
      </div>
      {/* Trainer name below */}
      {!peerMode && <Text className="text-app-yellow text-sm mobile:text-md tablet:text-lg laptop:text-lg mt-2">{name}</Text>}
      {/* <Text className="text-app-yellow text-sm mobile:text-md tablet:text-lg laptop:text-lg mt-2">{name}</Text> */}

      {/* Audio element */}
      {/* {isAudio && <audio ref={audioRef} autoPlay />} */}
    </div>
  );
};

export default Trainer;